import type { ErpSyncAuditEvent, ErpSyncAuditResponse } from '../../../services/api';
import type { ComboRebateOffer } from '../../ProductPricingBadge';
import { getEffectiveMapForProduct, resolveRebateDisplay } from '../../../utils/mapPricing';
import type { OverrideFormState } from '../../../utils/productDetailHelpers';
import type { CameraWithPricing, ProductPriceHistoryRow } from '../../../utils/productDetailHelpers';
import { pickMappedLiveListing } from '../../../utils/erpSyncLiveListing';
import { buildPricingWindows, mapFloorFor, PricingWindow, toMs } from './pricingWindows';

/**
 * Pure derivations behind the dealer product view. Everything here reads real
 * product, audit and override data; a fact the data does not carry is left
 * out rather than guessed.
 */

export const usd = (n: number | null | undefined): string =>
  n != null && Number.isFinite(Number(n))
    ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

export const shortDay = (ms: number | null | undefined, withYear = false): string =>
  ms == null
    ? ''
    : new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });

export const whenLabel = (iso: string | null | undefined): string => {
  const ms = toMs(iso);
  if (ms == null) return '—';
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// ─── Margin ──────────────────────────────────────────────────────────────────

/** Lead price minus dealer cost, plus a live dealer reimbursement; percent of the lead price. */
export function dealerMargin(lead: number | null, cost: number | null, reimbursement = 0): { amount: number; pct: number } | null {
  if (lead == null || !(lead > 0) || cost == null || !(cost > 0)) return null;
  const amount = Math.round((lead - cost + (reimbursement > 0 ? reimbursement : 0)) * 100) / 100;
  return { amount, pct: Math.round((amount / lead) * 1000) / 10 };
}

// ─── Price history delta ─────────────────────────────────────────────────────

const headlineOf = (v?: ProductPriceHistoryRow['newValue']): number | null => {
  if (!v) return null;
  const map = Number(v.mapPrice);
  if (map > 0) return Number(v.instantRebate) > 0 ? map - Number(v.instantRebate) : map;
  const sale = Number(v.salePrice);
  if (sale > 0) return sale;
  const retail = Number(v.retail);
  return retail > 0 ? retail : null;
};

/** Change in the advertised headline between a history row's old and new values; null when either side is unknown. */
export function historyDelta(row: ProductPriceHistoryRow): { label: string; direction: 'down' | 'up' } | null {
  if (row.changeType === 'sync_rejected') return null;
  const before = headlineOf(row.previousValue);
  const after = headlineOf(row.newValue);
  if (before == null || after == null) return null;
  const diff = Math.round((after - before) * 100) / 100;
  if (Math.abs(diff) < 0.005) return null;
  const abs = Math.abs(diff);
  const text = Number.isInteger(abs) ? `$${abs.toLocaleString('en-US')}` : usd(abs);
  return diff < 0 ? { label: `−${text}`, direction: 'down' } : { label: `+${text}`, direction: 'up' };
}

// ─── Classification ──────────────────────────────────────────────────────────

const FAMILY_WORDS: Array<[string, RegExp]> = [
  ['cinema camera', /cinema/],
  // Lens filters and other lens accessories (taxonomy `lens-accessories` > `lens-filter`, caps,
  // hoods, adapters) are matched before the generic lens family, so they are never read as a lens.
  ['filter', /filter/],
  ['lens accessory', /lens\s*(accessor|cap|hood|adapter|mount\s*adapter)|(cap|hood|adapter)s?\b/],
  ['lens', /\blens|lenses\b|\bzoom\b|\bprime\b/],
  ['camera', /camera|mirrorless|dslr|compact|body|bodies/],
  ['lighting', /flash|speedlight|light(ing)?\b|strobe/],
  ['tripod', /tripod|monopod|gimbal|support|head\b/],
  ['bag', /\bbag|backpack|case\b|sling/],
  ['strap', /strap/],
  ['battery', /batter|charger|power/],
  ['memory card', /memory|card|storage|ssd/],
  ['audio', /micro?phone|audio/],
  ['drone', /drone/],
];

/** The core families a generic "accessory" value can never stand for. */
const CORE_FAMILIES = new Set(['cinema camera', 'lens', 'camera']);

function familyOf(value: string): string | null {
  const v = value.toLowerCase().replace(/[-_›>/]+/g, ' ');
  for (const [family, re] of FAMILY_WORDS) if (re.test(v)) return family;
  if (/accessor/.test(v)) return 'accessory';
  return null;
}

const agree = (a: string, b: string) =>
  a === b || (a === 'accessory' && !CORE_FAMILIES.has(b)) || (b === 'accessory' && !CORE_FAMILIES.has(a));

export interface ClassificationRow {
  key: string;
  value: string;
  /** Null when the row agrees (or its family cannot be read); otherwise "Says <family>". */
  conflict: string | null;
}

export interface Classification {
  rows: ClassificationRow[];
  disagreements: number;
  /** One sentence on what a disagreement does to the store; null when every row agrees. */
  issue: string | null;
}

/**
 * The five classification fields, checked for agreement by reading each
 * value's product family from its words. A field whose family is not
 * readable is never flagged.
 */
export function buildClassification(product: Pick<CameraWithPricing, 'category' | 'categorySlug' | 'categoryPath' | 'productType' | 'productTypeExpanded'>): Classification | null {
  const raw: Array<[string, string | undefined]> = [
    ['categoryPath', product.categoryPath?.length ? product.categoryPath.join(' › ') : undefined],
    ['category', product.category || undefined],
    ['categorySlug', product.categorySlug || undefined],
    ['productType', product.productType || undefined],
    ['productTypeExpanded', product.productTypeExpanded || undefined],
  ];
  const present = raw.filter((r): r is [string, string] => !!r[1]);
  if (present.length < 2) return null;

  const families = present.map(([, v]) => familyOf(v));
  const votes = new Map<string, number>();
  families.forEach((f) => {
    if (f && f !== 'accessory') votes.set(f, (votes.get(f) ?? 0) + 1);
  });
  const ranked = Array.from(votes.entries()).sort((a, b) => b[1] - a[1]);
  const majority = ranked.length ? ranked[0][0] : families.includes('accessory') ? 'accessory' : null;
  // A tie at the top is not a majority: nothing is flagged.
  const tied = ranked.length > 1 && ranked[0][1] === ranked[1][1];

  const rows: ClassificationRow[] = present.map(([key, value], i) => {
    const f = families[i];
    const conflict = majority && !tied && f && !agree(f, majority) ? `Says ${f}` : null;
    return { key, value, conflict };
  });
  const bad = rows.filter((r) => r.conflict);
  let issue: string | null = null;
  if (bad.length && majority) {
    const others = rows.filter((r) => !r.conflict).map((r) => r.key);
    const first = bad[0];
    const plural = (n: number) => (n === 1 ? 'says' : 'say');
    issue = `${first.key} is "${first.value}", but ${others.join(' and ')} ${plural(others.length)} ${majority}. Shoppers filtering your store by ${majority} won't find it.`;
  }
  return { rows, disagreements: bad.length, issue };
}

// ─── Pricing schedule ────────────────────────────────────────────────────────

export interface ScheduleRow {
  startMs: number | null;
  endMs: number | null;
  dates: string;
  price: number | null;
  why: string;
  status: 'live' | 'upcoming';
}

const windowCovers = (w: PricingWindow, at: number) =>
  (w.startMs == null || w.startMs <= at) && (w.endExclusiveMs == null || w.endExclusiveMs > at);

/**
 * The lowest price the store may advertise, segment by segment, from now on:
 * MAP, MAP minus a running instant rebate, or a manufacturer sale price.
 * Segments split at every window boundary still ahead; a product with no MAP
 * is retail-priced and says it may be advertised lower.
 */
export function buildSchedule(product: CameraWithPricing, nowMs: number = Date.now()): ScheduleRow[] {
  const windows = buildPricingWindows(product, nowMs).filter((w) => w.status !== 'ended');
  const map = windows.find((w) => w.kind === 'map');
  const promos = windows.filter((w) => w.kind !== 'map');
  if (!map && !promos.length) {
    const retail = Number(product.pricing?.retail ?? product.price ?? 0);
    if (!(retail > 0)) return [];
    return [{ startMs: null, endMs: null, dates: 'Now', price: retail, why: 'Retail price. No MAP, so you can advertise lower.', status: 'live' }];
  }
  const cuts = new Set<number>([nowMs]);
  windows.forEach((w) => {
    if (w.startMs != null && w.startMs > nowMs) cuts.add(w.startMs);
    if (w.endExclusiveMs != null && w.endExclusiveMs > nowMs) cuts.add(w.endExclusiveMs);
  });
  const points = Array.from(cuts).sort((a, b) => a - b);
  const rows: ScheduleRow[] = [];
  points.forEach((start, i) => {
    const end = points[i + 1] ?? null;
    const probe = start + 1;
    const mapOn = map && windowCovers(map, probe) ? map.amount : null;
    const rebate = promos.find((w) => w.kind === 'rebate' && windowCovers(w, probe));
    const sale = promos.find((w) => w.kind === 'sale' && windowCovers(w, probe));
    let price: number | null = mapOn;
    let why = mapOn != null ? 'MAP only' : 'Retail price';
    if (rebate && mapOn != null) {
      price = Math.max(0, Math.round((mapOn - rebate.amount) * 100) / 100);
      why = `MAP ${usd(mapOn)} minus the ${usd(rebate.amount)} instant rebate`;
    }
    if (sale && (price == null || sale.amount < price)) {
      price = sale.amount;
      why = 'Manufacturer sale price';
    }
    if (price == null) return;
    const last = rows[rows.length - 1];
    if (last && last.price === price && last.why === why) {
      last.endMs = end;
      return;
    }
    rows.push({ startMs: start, endMs: end, dates: '', price, why, status: start <= nowMs ? 'live' : 'upcoming' });
  });
  rows.forEach((r) => {
    const from = r.status === 'live' && r.startMs === nowMs ? null : r.startMs;
    // A segment ends the instant the next starts; its last moment is 1 ms before, so a midnight
    // boundary names the day before and a timed one keeps its own date.
    const lastDay = r.endMs != null ? r.endMs - 1 : null;
    r.dates = from == null && lastDay == null
      ? 'Now'
      : from == null
        ? `Now – ${shortDay(lastDay)}`
        : lastDay == null
          ? `From ${shortDay(from)}`
          : `${shortDay(from)} – ${shortDay(lastDay)}`;
  });
  return rows;
}

// ─── Programs (rebates, bundles, promotions) ─────────────────────────────────

export interface ProgramRow {
  id: string;
  kind: 'Instant rebate' | 'Bundle' | 'Promotion';
  name: string;
  amount: string;
  status: 'live' | 'upcoming';
  statusLabel: string;
  meta: string;
  bundleCondition?: string;
}

/** Rebates, combo (bundle) offers and sale promotions the catalog carries for this product. */
export function buildPrograms(product: CameraWithPricing, nowMs: number = Date.now()): ProgramRow[] {
  const rows: ProgramRow[] = [];
  const windows = buildPricingWindows(product, nowMs).filter((w) => w.status !== 'ended');
  const range = (w: { startMs: number | null; endMs: number | null }) =>
    [w.startMs != null ? shortDay(w.startMs, true) : null, w.endMs != null ? shortDay(w.endMs, true) : null].filter(Boolean).join(' – ');
  const reimbursement = resolveRebateDisplay(product.pricing, product.pricingData).tradeCredit;
  windows.forEach((w) => {
    if (w.kind === 'map') return;
    const live = w.status === 'live';
    const metaParts = [range(w)];
    if (w.kind === 'rebate' && reimbursement > 0) metaParts.push(`Dealer reimbursement ${usd(reimbursement)}`);
    rows.push({
      id: `${w.kind}-${w.startMs}`,
      kind: w.kind === 'rebate' ? 'Instant rebate' : 'Promotion',
      name: w.kind === 'rebate' ? `${product.brand} instant rebate` : `${product.brand} sale price`,
      amount: w.kind === 'rebate' ? `−${usd(w.amount)}` : usd(w.amount),
      status: live ? 'live' : 'upcoming',
      statusLabel: live ? 'Live now' : `Starts ${shortDay(w.startMs)}`,
      meta: metaParts.filter(Boolean).join(' · '),
    });
  });
  const combos: ComboRebateOffer[] = product.pricingData?.comboOffers ?? [];
  combos
    .filter((o) => o.instantRebate > 0 && o.status !== 'expired')
    .forEach((o, i) => {
      const start = toMs(o.effectiveDate);
      const end = toMs(o.endDate);
      const live = o.status !== 'upcoming';
      rows.push({
        id: `combo-${i}`,
        kind: 'Bundle',
        name: o.condition ? `Sold with ${o.condition}` : 'Combo rebate',
        amount: `−${usd(o.instantRebate)}`,
        status: live ? 'live' : 'upcoming',
        statusLabel: live ? 'Live now' : start != null ? `Starts ${shortDay(start)}` : 'Upcoming',
        meta: [range({ startMs: start, endMs: end }), "Doesn't change this item's own price"].filter(Boolean).join(' · '),
        bundleCondition: o.condition || undefined,
      });
    });
  return rows;
}

export interface EndedRebateRow {
  id: string;
  /** "−$50" */
  amount: string;
  /** "Oct 5 – Oct 11, 2026" */
  dates: string;
  /** "Ended Oct 11" or "Ended 8 days ago" */
  endedLabel: string;
  /** What the lowest advertised price was while it ran, when known. */
  lowestAd: string | null;
}

/**
 * The last few instant rebates that already ended on this product, newest
 * first, so a dealer can see what expired and when the price went back to
 * MAP (MOT-551). Read off `buildPricingWindows`' ended rebate windows; a
 * live or upcoming rebate is never in this list.
 */
export function buildEndedRebates(product: CameraWithPricing, nowMs: number = Date.now(), limit = 3): EndedRebateRow[] {
  const ended = buildPricingWindows(product, nowMs)
    .filter((w) => w.kind === 'rebate' && w.status === 'ended' && w.endMs != null)
    .sort((a, b) => (b.endMs ?? 0) - (a.endMs ?? 0))
    .slice(0, limit);
  return ended.map((w) => {
    const endMs = w.endMs as number;
    const daysAgo = Math.floor((nowMs - endMs) / 86_400_000);
    const sameYear = new Date(endMs).getUTCFullYear() === new Date(nowMs).getUTCFullYear();
    return {
      id: `ended-${w.amount}-${w.startMs ?? ''}-${endMs}`,
      amount: `−${usd(w.amount)}`,
      dates: [w.startMs != null ? shortDay(w.startMs, !sameYear) : null, shortDay(endMs, true)].filter(Boolean).join(' – '),
      endedLabel: daysAgo <= 0 ? 'Ended today' : daysAgo === 1 ? 'Ended yesterday' : daysAgo < 30 ? `Ended ${daysAgo} days ago` : `Ended ${shortDay(endMs, !sameYear)}`,
      lowestAd: w.lowestAd != null ? usd(w.lowestAd) : null,
    };
  });
}

/** The live promo strip on Overview: the running rebate, else a running sale. */
export function livePromo(product: CameraWithPricing, nowMs: number = Date.now()): { title: string; body: string } | null {
  const programs = buildPrograms(product, nowMs).filter((p) => p.status === 'live' && p.kind !== 'Bundle');
  const first = programs[0];
  if (!first) return null;
  const end = buildPricingWindows(product, nowMs).find((w) => w.status === 'live' && w.kind !== 'map')?.endMs ?? null;
  const others = programs.length - 1 + (product.pricingData?.comboOffers?.filter((o) => o.instantRebate > 0 && o.status !== 'expired').length ?? 0);
  return {
    title: `${first.name} · ${first.amount.replace('−', '')} ${first.kind === 'Instant rebate' ? 'off' : ''}`.trim() + (end != null ? ` · ends ${shortDay(end)}` : ''),
    body: others > 0 ? `${others} more offer${others === 1 ? ' is' : 's are'} on this product. See the pricing calendar for every window.` : 'See the pricing calendar for every window and the lowest price you can advertise.',
  };
}

// ─── Store listing ───────────────────────────────────────────────────────────

export type ListingState = 'in_sync' | 'price_differs' | 'pending' | 'not_in_store';

export interface PushRow {
  id: string;
  when: string;
  result: 'Pushed' | 'Skipped' | 'Failed';
  summary: string;
  event: ErpSyncAuditEvent;
}

export interface CompareCell {
  label: string;
  live: string;
  note: string;
  warn: boolean;
}

export interface ListingModel {
  state: ListingState;
  platform: string;
  title: string;
  detail: string;
  adminUrl: string | null;
  compare: CompareCell[];
  pushes: PushRow[];
  liveError: string | null;
  sharedVariantNote: string | null;
}

/** Audit rows a Reset Shopify link writes: a mapping change, not a push. */
const SHOPIFY_LINK_RESET_CODE = 'shopify_link_reset';

export const platformLabel = (platform?: string | null): string =>
  platform === 'bigcommerce' ? 'BigCommerce'
    : platform === 'lightspeed' ? 'Lightspeed'
      : platform === 'lightspeed_ecom' ? 'Lightspeed eCom'
        : platform === 'shopify' ? 'Shopify'
          : 'store';

const moneyEqual = (a: number | null | undefined, b: number | null | undefined) =>
  (a == null && b == null) || (a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.01);

function pushSummary(e: ErpSyncAuditEvent): string {
  if (e.status === 'skipped' || e.status === 'error') {
    return e.error || (e.skipCode ? `Skipped: ${e.skipCode.replace(/_/g, ' ')}` : e.status === 'error' ? 'The push failed.' : 'Skipped.');
  }
  const parts: string[] = [];
  if (e.pushed?.price != null) parts.push(`Price ${usd(e.pushed.price)}`);
  if (e.pushed?.compareAtPrice != null) parts.push(`compare-at ${usd(e.pushed.compareAtPrice)}`);
  const changed = (e.fieldChanges ?? []).map((c) => c.field).filter((f) => !/price/i.test(f));
  if (changed.length) parts.push(`${changed.length} field${changed.length === 1 ? '' : 's'} changed`);
  const base = parts.join(', ') || (e.status === 'created' ? 'Listing created' : 'Listing updated');
  return e.status === 'created' ? `Created: ${base}` : base;
}

/** MOT-470: the skip code a Shopify push records when it left the store's own price or promo alone. */
export const STORE_PRICE_EDIT_CODE = 'price_changed_in_shopify';

export interface StorePriceEditTarget {
  integrationId: string;
  shopDomain: string | null;
  message: string;
}

/**
 * MOT-470: the Shopify integrations whose latest push of this product left a price or compare-at the
 * store changed in Shopify alone, with the push's own message. Each gets a Resume Motzu pricing action;
 * once a later push writes Motzu's price again the latest event no longer carries the code.
 */
export function storePriceEditTargets(audit: ErpSyncAuditResponse | undefined): StorePriceEditTarget[] {
  const latest = new Map<string, ErpSyncAuditEvent>();
  for (const e of audit?.events ?? []) {
    if (e.platform !== 'shopify' || !e.integrationId || e.skipCode === SHOPIFY_LINK_RESET_CODE) continue;
    const prev = latest.get(e.integrationId);
    if (!prev || (toMs(e.timestamp) ?? 0) > (toMs(prev.timestamp) ?? 0)) latest.set(e.integrationId, e);
  }
  return Array.from(latest.values())
    .filter((e) => e.skipCode === STORE_PRICE_EDIT_CODE)
    .map((e) => ({
      integrationId: e.integrationId as string,
      shopDomain: e.shopDomain ?? null,
      message: e.error || 'The price or promo on this listing was changed in Shopify, so Motzu left it as you set it.',
    }))
    .sort((a, b) => (a.shopDomain || a.integrationId).localeCompare(b.shopDomain || b.integrationId));
}

/**
 * The Your store listing card, from the sync audit read: whether the product
 * is live, what the live listing shows against the last push, and its recent
 * pushes. `inSync` is the store's sync selection; `catalogPrice` is what the
 * catalog says to advertise now.
 */
export function buildListingModel(
  audit: ErpSyncAuditResponse | undefined,
  inSync: boolean,
  catalogPrice: number | null,
): ListingModel {
  const { mapping, live } = pickMappedLiveListing(audit);
  const platform = platformLabel(mapping?.platform ?? (live ? 'shopify' : undefined));
  const lastPushed = mapping?.lastPushed ?? null;
  // Every integration's pushes go in the history (labelled when there is more than one);
  // only the compared listing's own integration decides its status.
  const allEvents = (audit?.events ?? [])
    .filter((e) => e.skipCode !== SHOPIFY_LINK_RESET_CODE)
    .sort((a, b) => (toMs(b.timestamp) ?? 0) - (toMs(a.timestamp) ?? 0));
  const events = allEvents.filter((e) => !mapping?.integrationId || e.integrationId === mapping.integrationId);
  const multiple = new Set(allEvents.map((e) => e.integrationId)).size > 1;
  const pushes: PushRow[] = allEvents.map((e) => ({
    id: e.id,
    when: whenLabel(e.timestamp),
    result: e.status === 'created' || e.status === 'updated' ? 'Pushed' : e.status === 'skipped' ? 'Skipped' : 'Failed',
    summary: `${multiple ? `${platformLabel(e.platform)}${e.shopDomain ? ` (${e.shopDomain})` : ''}: ` : ''}${pushSummary(e)}`,
    event: e,
  }));

  const wrongVariant = live?.skuMatched === false;
  const sharedVariantNote =
    live && (live.variantCount ?? 0) > 1 && !wrongVariant
      ? `${[live.variantTitle, live.sku ? `SKU ${live.sku}` : null, `${live.variantCount} variants on this listing`].filter(Boolean).join(' · ')}. Only this variant is compared.`
      : wrongVariant
        ? `The live price on the listing belongs to a different variant${lastPushed?.sku ? ` (this product is SKU ${lastPushed.sku})` : ''}, so it is not compared here.`
        : null;

  const livePrice = live && !live.error && !wrongVariant && live.price != null ? live.price : null;
  // A live read that failed, belongs to another variant or carries no price can't be compared.
  const comparable = livePrice != null && catalogPrice != null;
  const latest = events[0];
  const lastPushLabel = latest ? whenLabel(latest.timestamp) : lastPushed?.at ? whenLabel(lastPushed.at) : null;

  let state: ListingState;
  if (!inSync && !mapping) state = 'not_in_store';
  else if (!live || !comparable) state = mapping || inSync || live ? 'pending' : 'not_in_store';
  else if (!moneyEqual(livePrice, catalogPrice)) state = 'price_differs';
  else state = 'in_sync';

  const title =
    state === 'not_in_store' ? 'Not in your store'
      : state === 'pending'
        ? live ? `Live on your ${platform} store · not compared` : mapping ? `Mapped to your ${platform} store · no live listing read` : 'Selected for sync · no listing yet'
        : `Live on your ${platform} store · ${state === 'price_differs' ? 'price differs' : 'in sync'}`;

  const detail =
    state === 'not_in_store'
      ? `Add it to sync and the next run creates the listing on your ${platform === 'store' ? 'store' : `${platform} store`}.`
      : state === 'pending'
        ? live
          ? `${lastPushLabel ? `Last push ${lastPushLabel}. ` : ''}${live.error ? 'The live listing could not be read' : wrongVariant ? 'The live price belongs to a different variant' : 'The live listing has no price to compare'}, so it is not checked against the catalog.`
          : mapping
            ? `${lastPushLabel ? `Last push ${lastPushLabel}. ` : ''}The live listing could not be read, so nothing is compared.`
            : 'The next sync creates or links the listing.'
        : state === 'price_differs'
          ? latest?.status === 'skipped'
            ? `Your listing still shows ${usd(livePrice)}. The last sync skipped this product, so ${usd(catalogPrice)} never went out.`
            : `Your listing shows ${usd(livePrice)}; the catalog says ${usd(catalogPrice)}.`
          : `${lastPushLabel ? `Last push ${lastPushLabel}. ` : ''}The live price matches the catalog.`;

  const compare: CompareCell[] = [];
  if (live && !wrongVariant && !live.error) {
    const priceWarn = state === 'price_differs';
    compare.push({
      label: 'Price',
      live: usd(live.price),
      note: priceWarn ? `Catalog ${usd(catalogPrice)} · not pushed` : comparable ? 'Matches catalog' : 'Not compared',
      warn: priceWarn,
    });
    const caWarn = !!lastPushed && !moneyEqual(live.compareAtPrice, lastPushed.compareAtPrice);
    compare.push({
      label: 'Compare-at',
      live: live.compareAtPrice != null ? usd(live.compareAtPrice) : '—',
      note: caWarn ? `Last pushed ${usd(lastPushed?.compareAtPrice)}` : live.compareAtPrice != null ? 'MAP, while a rebate runs' : 'None set',
      warn: caWarn,
    });
    const skuWarn = !!lastPushed?.sku && !!live.sku && live.sku !== lastPushed.sku;
    compare.push({
      label: 'SKU',
      live: live.sku || '—',
      note: skuWarn ? `Last pushed ${lastPushed?.sku}` : 'Matches last push',
      warn: skuWarn,
    });
    compare.push({
      label: 'Listing updated',
      live: live.updatedAt ? shortDay(toMs(live.updatedAt), true) : '—',
      note: live.status ? `Status ${live.status.toLowerCase()}` : '',
      warn: false,
    });
  }

  return {
    state,
    platform,
    title,
    detail,
    adminUrl: live?.adminUrl ?? null,
    compare,
    pushes,
    liveError: live?.error ?? null,
    sharedVariantNote,
  };
}

/** What the catalog says to advertise now: a live sale, else MAP minus a live rebate, else retail. */
export function catalogAdvertisedPrice(product: CameraWithPricing, nowMs: number = Date.now()): number | null {
  const sale = buildPricingWindows(product, nowMs).find((w) => w.kind === 'sale' && w.status === 'live');
  if (sale) return sale.amount;
  const floor = mapFloorFor(product, nowMs);
  if (floor != null) return floor;
  return Number(product.pricing?.retail ?? product.pricing?.msrp ?? 0) || null;
}

// ─── Overrides ───────────────────────────────────────────────────────────────

/** Tag as the store types it: lowercased, spaces to hyphens, commas dropped. */
export const normalizeTag = (raw: string): string => raw.trim().toLowerCase().replace(/,/g, '').replace(/\s+/g, '-');

export const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

export interface CopyState {
  label: string;
  edited: boolean;
}

/** "Synced from catalog" / "Edited · not saved" / "Overridden" / "Reverts to catalog on save". */
export function copyState(current: string, saved: string): CopyState {
  if (current) return { label: current === saved ? 'Overridden' : 'Edited · not saved', edited: true };
  return { label: saved ? 'Reverts to catalog on save' : 'Synced from catalog', edited: false };
}

export interface OverrideValidation {
  floor: number | null;
  hasRebate: boolean;
  priceInvalid: boolean;
  belowFloor: boolean;
  /** Below the MAP floor while the store does not allow prices below MAP. */
  floorBlocking: boolean;
  dateRangeInvalid: boolean;
  /** Any of the above that stops a save. */
  blocked: boolean;
}

/** The checks a store override must pass before it can be saved; shared by the card and the dialog's unsaved-changes guard. */
export function validateOverride(product: CameraWithPricing, form: OverrideFormState, allowBelowMap: boolean, nowMs: number = Date.now()): OverrideValidation {
  const em = getEffectiveMapForProduct(product, nowMs);
  const rebate = resolveRebateDisplay(product.pricing, product.pricingData);
  const hasRebate = em?.windowStatus === 'active' && rebate.instantRebate > 0;
  const floor = em?.floor && em.floor > 0 ? em.floor : null;
  const text = form.overridePrice.trim();
  const num = Number(text.replace(/[$,]/g, ''));
  const priceInvalid = text !== '' && (!Number.isFinite(num) || num <= 0);
  const belowFloor = !priceInvalid && text !== '' && floor != null && num < floor - 0.005;
  const floorBlocking = belowFloor && !allowBelowMap;
  const dateRangeInvalid = !!(form.overridePriceStartAt && form.overridePriceEndAt && form.overridePriceEndAt < form.overridePriceStartAt);
  return { floor, hasRebate, priceInvalid, belowFloor, floorBlocking, dateRangeInvalid, blocked: priceInvalid || floorBlocking || dateRangeInvalid };
}
